<script lang="ts">
  import { onMount } from 'svelte';
  import { fade, scale } from 'svelte/transition';

  let show = $state(false);
  let dismissed = $state(false);
  let email = $state('');
  let status: 'idle' | 'submitting' | 'success' = $state('idle');

  onMount(() => {
    // Check if they've already seen it this session
    const hasSeen = localStorage.getItem('bleunk-exit-popup-seen');
    if (hasSeen) return;

    const handleMouseOut = (e: MouseEvent) => {
      if (dismissed || show) return;
      // If mouse leaves the top of the viewport
      if (e.clientY <= 0) {
        show = true;
        localStorage.setItem('bleunk-exit-popup-seen', 'true');
      }
    };

    document.addEventListener('mouseleave', handleMouseOut);
    return () => document.removeEventListener('mouseleave', handleMouseOut);
  });

  function close() {
    show = false;
    dismissed = true;
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    status = 'submitting';

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    try {
      // 1. Submit to Netlify (Root) - This ensures it shows up in the Dashboard
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString()
      });

      // 2. Submit to our API - This triggers the Resend Email
      const response = await fetch('/api/blueprint', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        status = 'success';
        setTimeout(close, 2000);
      } else {
        throw new Error('Email API failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      status = 'idle';
      alert('Something went wrong. Please try again.');
    }
  }
</script>

{#if show}
  <div 
    class="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-void/80 backdrop-blur-sm"
    transition:fade={{ duration: 200 }}
    onclick={close}
    role="button"
    tabindex="0"
    onkeydown={(e) => e.key === 'Escape' && close()}
  >
    <div 
      class="relative w-full max-w-lg bg-card border border-[#C9A87C]/30 rounded-3xl p-8 sm:p-12 shadow-2xl overflow-hidden"
      transition:scale={{ duration: 300, start: 0.95 }}
      onclick={(e) => e.stopPropagation()}
      role="presentation"
    >
      <!-- Background Glow -->
      <div class="absolute -top-24 -right-24 w-64 h-64 bg-[#C9A87C]/10 rounded-full blur-[80px] pointer-events-none"></div>

      <button 
        onclick={close}
        class="absolute top-6 right-6 text-dim-30 hover:text-content transition-colors"
        aria-label="Close"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <div class="relative z-10 text-center">
        <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C9A87C]/20 bg-[#C9A87C]/5 text-[#C9A87C] text-[10px] font-bold uppercase tracking-widest mb-6">
          Free Blueprint
        </div>
        
        <h2 class="font-display font-bold text-3xl mb-4 text-content leading-tight">
          Wait! Don't leave your operations to chance.
        </h2>
        
        <p class="text-dim-50 text-base mb-8 leading-relaxed">
          Get the <span class="text-content font-semibold">"Invisible Workforce" Blueprint</span> — our internal 7-day automation audit template to find the biggest leaks in your business.
        </p>

        {#if status === 'success'}
          <div class="py-4 px-6 rounded-2xl bg-[#7A9E90]/10 border border-[#7A9E90]/20 text-[#7A9E90] animate-fade-up">
            <p class="font-bold">Sent! Check your inbox.</p>
          </div>
        {:else}
          <form 
            name="exit-popup-blueprint"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onsubmit={handleSubmit} 
            class="space-y-3"
          >
            <!-- Netlify Hidden Fields -->
            <input type="hidden" name="form-name" value="exit-popup-blueprint" />
            <p class="hidden">
              <label>Don’t fill this out if you’re human: <input name="bot-field" /></label>
            </p>

            <input 
              type="email" 
              name="email"
              bind:value={email}
              placeholder="work@company.com" 
              required
              class="w-full px-5 py-4 rounded-2xl bg-void border border-dim-10 text-content placeholder:text-dim-30 focus:outline-none focus:ring-2 focus:ring-[#C9A87C]/50 transition-all text-sm"
            />
            <button 
              type="submit"
              disabled={status === 'submitting'}
              class="w-full px-8 py-4 rounded-2xl bg-[#C9A87C] text-[#08080E] font-bold text-sm hover:bg-[#D4B896] transition-all disabled:opacity-50 shadow-lg shadow-[#C9A87C]/20"
            >
              {status === 'submitting' ? 'Sending...' : 'Send me the Blueprint →'}
            </button>
          </form>
          <p class="mt-4 text-[10px] text-dim-30 italic">
            Joined by 140+ founders optimizing their operational velocity.
          </p>
        {/if}
      </div>
    </div>
  </div>
{/if}
